import { client } from '../common/client';
import type {
  CreateReviewRequest,
  CreateReviewResponse,
  GetCuratedReviewsResponse,
  GetAIProductReviewsRequest,
  GetAIProductReviewsResponse,
  GetProductReviewsRequest,
  GetProductReviewsResponse,
  ReviewStatisticsRequest,
  ReviewStatisticsResponse,
} from './utils/types';
import { ReviewStatus } from '../../constants';

class ReviewAPIClient {
  public async getProductReviews(
    productId: number,
    params?: Omit<GetProductReviewsRequest, 'productId'>,
  ): Promise<GetProductReviewsResponse> {
    return await client.get(`/reviews/product/${productId}`, { params }).then((res) => res.data);
  }

  public async getCuratedReviews(): Promise<GetCuratedReviewsResponse> {
    return await client.get('/reviews/curated').then((res) => res.data);
  }

  public async getAIProductReviews(
    productId: number,
    params?: Omit<GetAIProductReviewsRequest, 'productId'>,
  ): Promise<GetAIProductReviewsResponse> {
    return await client
      .get(`/reviews/product/${productId}`, {
        params: {
          ...params,
          tags: 'AIReview',
          status: ReviewStatus.Submitted,
        },
      })
      .then((res) => res.data);
  }

  public async getReviewStatistics(params: ReviewStatisticsRequest): Promise<ReviewStatisticsResponse> {
    return await client.get('/reviews/statistics', { params }).then((res) => res.data);
  }

  public async createReview(params: CreateReviewRequest): Promise<CreateReviewResponse> {
    function createFormData(params: CreateReviewRequest): FormData {
      const formData = new FormData();

      if (params.reviewId !== undefined) {
        formData.append('reviewId', String(params.reviewId));
      }
      formData.append('name', params.name);
      formData.append('rating', String(params.rating));
      formData.append('body', params.body);
      formData.append('productId', String(params.productId));

      if (params.title) {
        formData.append('title', params.title);
      }

      formData.append('skinType', params.skinType.join(','));
      formData.append('skinConcern', params.skinConcern.join(','));
      if (params.email) {
        formData.append('email', params.email);
      }

      if (params.files?.length) {
        params.files.forEach((file) => {
          formData.append('files', file, file.name);
        });
      }

      return formData;
    }

    return await client
      .post('/reviews', createFormData(params), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => res.data);
  }
}

export const reviewAPIClient = new ReviewAPIClient();
